import Dimension from '@/Enums/ReportBuilder/Dimension.enum';
import DimensionGroup from '@/Enums/ReportBuilder/DimensionGroup.enum';
import Metric from '@/Enums/ReportBuilder/Metric.enum';
import MetricGroup from '@/Enums/ReportBuilder/MetricGroup.enum';
import type { IGroupedControlOption } from '@/Interfaces/ControlOption.interface';
import { hydrateMetricOptionGroup, hydrateOptionGroup } from '.';

interface IGetDimensionOptions {
  includeOrganization?: boolean;
  includeRateCard?: boolean;
  includeLineItemAltId?: boolean;
  includeCampaignAltId?: boolean;
  includeAdvertiserAltId?: boolean;
  includeClientExternalId?: boolean;
}

export const attributionDimensions: Dimension[] = [Dimension.EVENT_ID, Dimension.EVENT_NAME];

export const getDimensionOptions = (options?: IGetDimensionOptions): IGroupedControlOption<string, Dimension>[] => [
  ...hydrateOptionGroup(
    [
      options?.includeOrganization ? Dimension.ORGANIZATION_NAME : undefined,
      Dimension.CLIENT_NAME,
      options?.includeClientExternalId ? Dimension.CLIENT_EXTERNAL_ID : undefined,
      Dimension.ADVERTISER_NAME,
      options?.includeAdvertiserAltId ? Dimension.ADVERTISER_ALT_ID : undefined,
      Dimension.CAMPAIGN_NAME,
      options?.includeCampaignAltId ? Dimension.CAMPAIGN_ALT_ID : undefined,
      Dimension.LINE_ITEM_NAME,
      options?.includeLineItemAltId ? Dimension.LINE_ITEM_ALT_ID : undefined,
      Dimension.CAMPAIGN_START_DATE,
      Dimension.CAMPAIGN_END_DATE,
      Dimension.LINE_ITEM_START_DATE,
      Dimension.LINE_ITEM_END_DATE,
    ].filter(Boolean) as Dimension[],
    DimensionGroup.HighLevel,
  ),
  ...hydrateOptionGroup(
    [Dimension.CAMPAIGN_BOOKED_IMPRESSIONS, Dimension.LINE_ITEM_BOOKED_IMPRESSIONS],
    DimensionGroup.BookedTotals,
  ),
  ...hydrateOptionGroup(
    [Dimension.CREATIVE_NAME, Dimension.CREATIVE_ID, Dimension.CREATIVE_DURATION, Dimension.CREATIVE_FORMAT],
    DimensionGroup.Creative,
  ),
  ...hydrateOptionGroup(
    [
      Dimension.DIGITAL_CHANNEL,
      Dimension.PUBLISHER_NAME,
      Dimension.CONTENT_NETWORK,
      Dimension.CONTENT_CHANNEL,
      Dimension.DEVICE_TYPE,
      Dimension.CONTENT_GENRE,
      Dimension.CONTENT_TITLE,
    ],
    DimensionGroup.Inventory,
  ),
  ...hydrateOptionGroup([Dimension.STATE, Dimension.DMA, Dimension.CITY, Dimension.ZIP], DimensionGroup.Geography),
  ...hydrateOptionGroup([Dimension.DAYPART, Dimension.DAY_OF_WEEK, Dimension.HOUR_OF_DAY], DimensionGroup.Daypart),
  ...hydrateOptionGroup(
    options?.includeRateCard ? [Dimension.RATE_CARD, Dimension.SOLD_CPM] : [Dimension.SOLD_CPM],
    DimensionGroup.CPM,
  ),
  // ...hydrateOptionGroup([Dimension.AUDIENCE], DimensionGroup.Audience),
  ...hydrateOptionGroup(attributionDimensions, DimensionGroup.Conversions),
];

interface IGetMetricOptions {
  includeClientSpend?: boolean;
  includeTotalSpend?: boolean;
}

export const getMetricOptions = (options?: IGetMetricOptions): IGroupedControlOption<string, Metric>[] => [
  ...hydrateMetricOptionGroup(
    [
      Metric.IMPRESSIONS,
      Metric.REACH,
      Metric.FREQUENCY,
      Metric.VIDEO_COMPLETES,
      Metric.CLICKS,
      Metric.VCR,
      Metric.VIDEO_PLAYS,
      Metric.VIDEO_Q1S,
      Metric.VIDEO_MIDPOINTS,
      Metric.VIDEO_Q3S,
      Metric.CTR,
      Metric.IP_CONVERSIONS,
      Metric.AUDIO_PLAYS,
      Metric.AUDIO_COMPLETES,
      Metric.AUDIO_Q1S,
      Metric.AUDIO_MIDPOINTS,
      Metric.AUDIO_Q3S,
      Metric.AUDIO_SKIPS,
      Metric.ACR,
    ],
    MetricGroup.Delivery,
  ),
  ...hydrateMetricOptionGroup([Metric.BOOKED, Metric.PACING_TO, Metric.PACING_TO_PERCENT], MetricGroup.Pacing),
  ...hydrateMetricOptionGroup(
    [
      options?.includeClientSpend ? Metric.CLIENT_SPEND : undefined,
      options?.includeClientSpend ? Metric.CLIENT_SPEND_CPA : undefined,
      options?.includeTotalSpend ? Metric.TOTAL_SPEND : undefined,
      options?.includeTotalSpend ? Metric.TOTAL_SPEND_CPA : undefined,
      // Spend/CPM share Total Spend's gate (canSeeTotalSpend on the backend): CPM-allowed role plus
      // the whitelabel's showCpm toggle, which is how the Reporting Dashboard scopes these to
      // Percentage of Media clients. No separate flag needed (#1927).
      options?.includeTotalSpend ? Metric.SPEND : undefined,
      options?.includeTotalSpend ? Metric.CPM : undefined,
    ].filter(Boolean) as Metric[],
    MetricGroup.Financial,
  ),
];

export const pacingMetrics: Metric[] = [Metric.BOOKED, Metric.PACING_TO, Metric.PACING_TO_PERCENT];

// Mirrors the backend PACING_INCOMPATIBLE_METRICS guard. Reach/Frequency are non-additive; the CPA
// metrics are ratios with no rate projection in the pacing query; Total Spend/Spend/CPM's
// multipliers are resolved per request, which the pacing CTE cannot embed. Client Spend is
// projected there, so it stays compatible.
export const pacingIncompatibleMetrics: Metric[] = [
  Metric.REACH,
  Metric.FREQUENCY,
  Metric.TOTAL_SPEND,
  Metric.CLIENT_SPEND_CPA,
  Metric.TOTAL_SPEND_CPA,
  Metric.SPEND,
  Metric.CPM,
];

// Dimensions that don't disable the pacing metrics: line-item grain and its ancestors
// (Organization / Client / Advertiser / Campaign). Must mirror the backend
// PACING_COMPATIBLE_DIMENSIONS guard.
// NOTE: CAMPAIGN_ALT_ID / ADVERTISER_ALT_ID are intentionally absent — referenced elsewhere (labels,
// getDimensionOptions) but not real Dimension enum members, so they can't be selected here.
export const pacingCompatibleDimensions: Dimension[] = [
  Dimension.ORGANIZATION_NAME,
  Dimension.CLIENT_NAME,
  Dimension.CLIENT_EXTERNAL_ID,
  Dimension.ADVERTISER_NAME,
  Dimension.LINE_ITEM_NAME,
  Dimension.LINE_ITEM_ALT_ID,
  Dimension.LINE_ITEM_START_DATE,
  Dimension.LINE_ITEM_END_DATE,
  Dimension.LINE_ITEM_BOOKED_IMPRESSIONS,
  Dimension.CAMPAIGN_NAME,
  Dimension.CAMPAIGN_START_DATE,
  Dimension.CAMPAIGN_END_DATE,
  Dimension.CAMPAIGN_BOOKED_IMPRESSIONS,
];

export const performanceOnlyDimensions: Dimension[] = [
  Dimension.DIGITAL_CHANNEL,
  Dimension.DEVICE_TYPE,
  Dimension.PUBLISHER_NAME,
  Dimension.CONTENT_NETWORK,
  Dimension.CONTENT_CHANNEL,
  Dimension.CONTENT_GENRE,
  Dimension.CONTENT_TITLE,
];
